import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { AIDisclosureResultState, DisclosureResult } from '../type/ai-disclosure.type';
import { AIDisclosureParagraphEditType, AIDisclosureParagraphType, AIDisclosureStep } from '../type/ai-disclosure.enum';
import { HistoryService } from '@/services/history/service/history.service';
import { AIDisclosureResultModel } from '../model/ai-disclosure-result.model';
import {
    GetDisclosureTitleDTO,
    ExportDisclosureDTO,
    PutDisclosureSettingDTO,
} from '@/services/ai-disclosure/service/ai-disclosure.repository.interface';
import { AIDisclosureModel } from '@/services/ai-disclosure/model/ai-disclosure.model';
import { StreamStatus } from '@/services/ai-translation/type/ai-translation.enum';
import type { SSEStreamService } from '@patsnap-utils/sse';
import {
    ApiGetDisclosureContentRequest, type ApiGetDisclosureContentResponse,
    GetDisclosureContent,
} from '@/infrastructure/api-functions/interface/get-disclosure-content.interface';
import { I18nService } from '@analytics/shared/services/i18n.interface';

export const AIDisclosureResultService = Symbol('AIDisclosureResultService');

export interface AIDisclosureResultService {
    getModelData: () => AIDisclosureResultState;
    readonly onStatesChange: Event<AIDisclosureResultState>;
    generateContent: (params: {
        contentType: AIDisclosureParagraphEditType,
        text?: string
    }, callback: (status: string, content?: string) => void) => void;
    generateResult: () => void;
    stopGenerate: () => void;
    updateTitle: (newTitle: string) => void;
    updateDisclosureResult: (paragraphType: AIDisclosureParagraphType, content: string, isAddition?: boolean) => void;
    updateSetting: (settings: AIDisclosureResultState['settings']) => void;
    toggleParagraphVisibleStatus: (type: AIDisclosureParagraphType) => void;
    exportDisclosure: (result: DisclosureResult[]) => void;
    initModel: (modelData: Partial<AIDisclosureResultState>) => void;
}

@injectable()
export class AIDisclosureResultServiceImpl implements AIDisclosureResultService {
    private _stream: SSEStreamService<ApiGetDisclosureContentResponse> | null = null;
    private _onStatesChangeEmitter = new Emitter<AIDisclosureResultState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    // eslint-disable-next-line max-params
    constructor(
        @inject(AIDisclosureModel)
        private _disclosureModel: AIDisclosureModel,
        @inject(AIDisclosureResultModel)
        private _model: AIDisclosureResultModel,
        @inject(HistoryService)
        private _historyService: HistoryService,
        @inject(GetDisclosureTitleDTO)
        private _getDisclosureTitleDTO: GetDisclosureTitleDTO,
        @inject(ExportDisclosureDTO)
        private _exportDisclosureDTO: ExportDisclosureDTO,
        @inject(GetDisclosureContent)
        private _getDisclosureContent: GetDisclosureContent,
        @inject(PutDisclosureSettingDTO)
        private _putDisclosureSettingDTO: PutDisclosureSettingDTO,
        @inject(I18nService)
        private _i18n: I18nService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<AIDisclosureResultState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public initModel(modelData: Partial<AIDisclosureResultState>) {
        this._setModelData(modelData);
    }

    public updateTitle(newTitle: string) {
        this._historyService.putHistory({
            detail_key_values: {
                '$.title': newTitle,
            },
        });
        this._setModelData({
            title: newTitle,
        });
    }

    public updateSetting(settings: AIDisclosureResultState['settings']) {
        const { taskId } = this._getTaskInfo();
        this._putDisclosureSettingDTO.fetch({ ...settings, taskId, currentStep: AIDisclosureStep.DISCLOSURE_RESULT });

        this._setModelData({
            settings,
        });
    }

    public toggleParagraphVisibleStatus(type: AIDisclosureParagraphType) {
        const { hidden } = this.getModelData();
        const index = hidden.indexOf(type);
        if (index !== -1) {
            hidden.splice(index, 1);
        } else {
            hidden.push(type);
        }
        this._setModelData({ hidden });

        this._historyService.putHistory({
            detail_key_values: {
                '$.hidden': hidden,
            },
        });
    }

    private _getTaskInfo() {
        const { taskId } = this._historyService.getModelData().historyDetailTemp;
        const { currentStep } = this._disclosureModel.modelData;
        const model = this.getModelData();
        return { taskId, currentStep, ...model };
    }

    public async exportDisclosure(data: DisclosureResult[]) {
        const { taskId, title } = this._getTaskInfo();
        const wordUrl = await this._exportDisclosureDTO.fetch({ data, taskId, title });
        window.open(wordUrl, '_self');
    }

    private async _getDisclosureTitle() {
        const { taskId, currentStep } = this._getTaskInfo();
        const disclosureTitle = await this._getDisclosureTitleDTO.fetch({ taskId, currentStep });

        this._setModelData({ title: disclosureTitle });
        this._historyService.updateTitle(disclosureTitle || this._i18n.t('aiDisclosure.untitled'));
    }

    public updateDisclosureResult(paragraphType: AIDisclosureParagraphType, content: string, isAddition?: boolean) {
        const { disclosureResult } = this.getModelData();
        const index = disclosureResult.findIndex(item => item.paragraphType === paragraphType);
        if (index !== -1) {
            if (isAddition) {
                disclosureResult[index].content += content;
            } else {
                disclosureResult[index].content = content;
            }
            this._setModelData({ disclosureResult });

            this._historyService.putHistory({
                detail_key_values: {
                    [`$.${ paragraphType }`]: disclosureResult[index].content,
                },
            });
        }
    }

    public stopGenerate() {
        this._stream?.close();
    }

    public async generateContent(params: {
        contentType: AIDisclosureParagraphEditType,
        text?: string
    }, callback: (status: string, content?: string) => void) {
        const { contentType, text } = params;
        const { taskId, currentStep } = this._getTaskInfo();

        this._stream = this._getDisclosureContent.getDisclosureContent({
            task_id: taskId,
            current_step: currentStep,
            content_type: contentType,
            ...(text ? { text } : {}),
        })
            .onMessage(data => {
                const { status, content } = data.data ?? {};
                if (status === StreamStatus.FINISH) {
                    callback(status, content);
                    this._stream?.close();
                } else {
                    if (content.length) {
                        callback(status, content);
                    }
                }
            })
            .onError(() => {
                callback('ERROR');
            });

        this._stream && this._stream.start();
    }

    public async generateResult() {
        const { taskId, currentStep, title } = this._getTaskInfo();

        this._setModelData({ disclosureResult: [] });
        if (!title) {
            await this._getDisclosureTitle();
        }

        const generatedParagraphs = [];
        await this._historyService.putHistory({
            detail_key_values: {
                '$.generated_paragraphs': generatedParagraphs,
            },
        });

        for (const paragraphType of Object.values(AIDisclosureParagraphType)) {
            await this._fetchStreamData({
                task_id: taskId,
                current_step: currentStep,
                content_type: paragraphType,
            });

            this._updateDisclosureResult({ paragraphType, isGenerated: true });
        }
    }

    public status = StreamStatus.PROGRESS_WITHOUT_DATA;

    private _updateDisclosureResult = ({ content = '', paragraphType, isGenerated = false }: { content?: string, paragraphType: AIDisclosureParagraphType, isGenerated?: boolean }) => {
        const { disclosureResult } = this.getModelData();
        const idx = disclosureResult.findIndex(item => item.paragraphType === paragraphType);

        if (isGenerated && disclosureResult[idx]) {
            // 控制下一段内容的title显示
            disclosureResult[idx].isGenerated = true;
        }

        if (content.length && !isGenerated) {
            if (idx !== -1) {
                disclosureResult[idx].content = content;
            } else {
                disclosureResult.push({
                    paragraphType: paragraphType,
                    isGenerated: false,
                    content: content,
                });
            }
        }

        this._setModelData({ disclosureResult });
    };

    private async _fetchStreamData(params: ApiGetDisclosureContentRequest<AIDisclosureParagraphType>): Promise<void> {
        return new Promise((resolve, reject) => {
            this._stream = this._getDisclosureContent.getDisclosureContent(params)
                .onMessage(data => {
                    const { status, content } = data.data ?? {};
                    if (status === StreamStatus.FINISH) {
                        resolve();
                        this._stream?.close();
                    } else {
                        this._updateDisclosureResult({ content, paragraphType: params.content_type });
                    }
                })
                .onError((err: any) => {
                    reject(err);
                });

            this._stream && this._stream.start();
        });
    }
}
